import React from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

interface EngagementDataPoint {
  date: string;
  engagement: number;
  participation: number;
  focus: number;
}

interface StudentEngagementChartProps {
  studentId?: number;
  data?: EngagementDataPoint[];
  timeframe?: string;
}

export const StudentEngagementChart: React.FC<StudentEngagementChartProps> = ({ 
  data = [],
  timeframe = '30 days' 
}) => {
  // Simple bar chart implementation
  const maxValue = Math.max(...data.map(d => Math.max(d.engagement, d.participation, d.focus)));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Student Engagement Trends</CardTitle>
        <p className="text-sm text-gray-600">Last {timeframe}</p>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No engagement data available</p>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-between text-sm text-gray-600 mb-4">
              <span className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded mr-2"></div>
                Engagement
              </span>
              <span className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded mr-2"></div>
                Participation
              </span>
              <span className="flex items-center">
                <div className="w-3 h-3 bg-purple-500 rounded mr-2"></div>
                Focus
              </span>
            </div>
            
            <div className="space-y-3">
              {data.slice(-7).map((point, index) => (
                <div key={index} className="space-y-1">
                  <div className="text-xs text-gray-500">{point.date}</div>
                  <div className="flex space-x-1 h-6">
                    <div 
                      className="bg-blue-500 rounded"
                      style={{ width: `${(point.engagement / maxValue) * 100}%` }}
                      title={`Engagement: ${point.engagement}%`}
                    />
                    <div 
                      className="bg-green-500 rounded"
                      style={{ width: `${(point.participation / maxValue) * 100}%` }}
                      title={`Participation: ${point.participation}%`}
                    />
                    <div 
                      className="bg-purple-500 rounded"
                      style={{ width: `${(point.focus / maxValue) * 100}%` }}
                      title={`Focus: ${point.focus}%`}
                    />
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center text-sm text-gray-500 mt-4">
              Showing last 7 data points
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}; 